import { useEffect } from 'react'
import { Alert, Col, Form, Input, InputNumber, Modal, Row, Select, Switch } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { getCurrencyCode } from '@/shared/lib/format'
import { money, type Money } from '@/shared/lib/money'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { SUPPLIER_CATEGORIES, saveSupplier, type Supplier, type SupplierCategory } from './supplierApi'

interface SupplierFormModalProps {
  open: boolean
  supplier: Supplier | null
  onClose: () => void
  onSaved: (supplier: Supplier) => void
}

interface FormValues {
  name: string
  category: SupplierCategory
  contactPerson?: string
  phone?: string
  whatsapp?: string
  email?: string
  address?: string
  taxId?: string
  bankDetails?: string
  contractRef?: string
  paymentTermsDays: number
  creditLimit?: Money | null
  active: boolean
  notes?: string
}

const text = (value: string | undefined) => (value?.trim() ? value.trim() : null)

export function SupplierFormModal({ open, supplier, onClose, onSaved }: SupplierFormModalProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<FormValues>()
  const currency = user?.tenant.currency ?? getCurrencyCode()

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({
      name: supplier?.name ?? '',
      category: supplier?.category ?? 'NETWORK_EQUIPMENT',
      contactPerson: supplier?.contactPerson ?? '',
      phone: supplier?.phone ?? '',
      whatsapp: supplier?.whatsapp ?? '',
      email: supplier?.email ?? '',
      address: supplier?.address ?? '',
      taxId: supplier?.taxId ?? '',
      bankDetails: supplier?.bankDetails ?? '',
      contractRef: supplier?.contractRef ?? '',
      paymentTermsDays: supplier?.paymentTermsDays ?? 0,
      creditLimit: supplier?.creditLimitMinor == null ? null : money(supplier.creditLimitMinor, currency),
      active: supplier?.active ?? true,
      notes: supplier?.notes ?? '',
    })
  }, [open, supplier, form, currency])

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      saveSupplier(supplier?.id ?? null, {
        name: values.name.trim(),
        category: values.category,
        contactPerson: text(values.contactPerson),
        phone: text(values.phone),
        whatsapp: text(values.whatsapp),
        email: text(values.email),
        address: text(values.address),
        taxId: text(values.taxId),
        bankDetails: text(values.bankDetails),
        contractRef: text(values.contractRef),
        paymentTermsDays: values.paymentTermsDays,
        creditLimitMinor: values.creditLimit?.amount ?? null,
        active: values.active,
        notes: text(values.notes),
      }),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.supplier.all })
      onSaved(saved)
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={supplier ? t('supplier.form.editTitle', { code: supplier.code }) : t('supplier.form.createTitle')}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      onCancel={close}
      confirmLoading={save.isPending}
      destroyOnHidden
      width={760}
    >
      {save.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(save.error, t)} />
      ) : null}
      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Row gutter={16}>
          <Col xs={24} md={14}>
            <Form.Item
              name="name"
              label={t('supplier.field.name')}
              rules={[{ required: true, whitespace: true, max: 200, message: t('supplier.validation.name') }]}
            >
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={24} md={10}>
            <Form.Item name="category" label={t('supplier.field.category')} rules={[{ required: true, message: t('supplier.validation.category') }]}>
              <Select
                options={SUPPLIER_CATEGORIES.map((value) => ({ value, label: t(`supplier.category.${value}`) }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="contactPerson" label={t('supplier.field.contactPerson')}>
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="phone" label={t('supplier.field.phone')}>
              <Input maxLength={32} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="whatsapp" label={t('supplier.field.whatsapp')}>
              <Input maxLength={32} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="email" label={t('supplier.field.email')} rules={[{ type: 'email', message: t('supplier.validation.email') }]}>
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="taxId" label={t('supplier.field.taxId')}>
              <Input maxLength={64} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="address" label={t('supplier.field.address')}>
              <Input maxLength={500} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="bankDetails" label={t('supplier.field.bankDetails')}>
              <Input.TextArea rows={2} maxLength={1000} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="contractRef" label={t('supplier.field.contractRef')}>
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="paymentTermsDays"
              label={t('supplier.field.paymentTermsDays')}
              extra={t('supplier.field.paymentTermsHint')}
              rules={[{ required: true, message: t('supplier.validation.terms') }]}
            >
              <InputNumber min={0} max={365} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={10}>
            <Form.Item name="creditLimit" label={t('supplier.field.creditLimit')} extra={t('supplier.field.creditLimitHint')}>
              <MoneyInput currency={currency} minMinor={0} />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item name="active" label={t('supplier.field.active')} valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="notes" label={t('supplier.field.notes')}>
              <Input.TextArea rows={2} maxLength={2000} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
